import {
  add,
  always,
  append,
  applySpec,
  assoc,
  compose,
  defaultTo,
  dissoc,
  evolve,
  filter,
  ifElse,
  isNil,
  map,
  modify,
  not,
  omit,
  pipe,
  prop,
  propEq,
  reduce,
  split,
  when
} from 'ramda';
import { dateTimeToMidday, dateTsToFr } from '@@viewHelpers/date';
import { isNilOrEmpty, isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import moment from 'moment';
import React from 'react';
import { extraFormatProject } from '../extra/extraFormatProject';

/**
 * Add an beneficiaryIndex to each pack to identify the beneficiary
 * The beneficiaryIndex is incremented each time the current pack has a different beneficiary (id prop)
 */
const addBeneficiaryIndexToPacks = (packs, commissionRate) =>
  compose(
    prop('packs'),
    reduce(
      (acc, pack) => {
        const currentId = prop('granteeId', pack) || prop('customerId', pack);

        if (isNil(currentId)) {
          return acc;
        }

        const findCommission = compose(
          filter((price) => parseInt(price.commission) === commissionRate),
          defaultTo([])
        );

        if (not(propEq('id', currentId, acc))) {
          acc = pipe(modify('index', add(1)), assoc('id', currentId))(acc);
        }
        pack = compose(
          evolve({
            pricings: map(
              evolve({
                prices: findCommission
              })
            )
          }),
          assoc('beneficiaryIndex', prop('index', acc))
        )(pack);

        acc = modify('packs', append(pack), acc);
        return acc;
      },
      {
        index: -1,
        id: null,
        packs: []
      }
    ),
    defaultTo([])
  )(packs);

export const formatPacks = (packs, commissionRate) =>
  addBeneficiaryIndexToPacks(packs, commissionRate);

export const formatOffer = compose(
  evolve({
    effectDate: when(isNotNilOrEmpty, dateTsToFr),
    packs: when(isNotNilOrEmpty, formatPacks)
  }),
  dissoc('minEffectDate'),
  dissoc('maxEffectDate')
);

export const formatSocialSecurity = evolve({
  dateCSS: when(isNotNilOrEmpty, dateTsToFr)
});

export const formatCustomer = evolve({
  birthDate: when(isNotNilOrEmpty, dateTsToFr),
  socialSecurities: ifElse(isNotNilOrEmpty, map(formatSocialSecurity), always([]))
});

export const formatGrantee = evolve({
  birthDate: when(isNotNilOrEmpty, dateTsToFr)
});

// used when we get data from backend, some components need values to be formatted
export const formatProject = compose(
  extraFormatProject,
  evolve({
    offer: when(isNotNilOrEmpty, formatOffer),
    customer: when(isNotNilOrEmpty, formatCustomer),
    grantees: when(isNotNilOrEmpty, map(formatGrantee))
  }),
  dissoc('jobs')
);

// date normalisée à midi pour éviter les pb de fuseaux horaires
export const frDateToMiddayDateString = (dateStr) => {
  const momentDate = moment(dateStr, 'DD/MM/YYYY', true);
  return momentDate.isValid() ? dateTimeToMidday(momentDate).valueOf().toString() : null;
};

export const formatSocialSecurityBeforeSubmit = evolve({
  dateCSS: when(isNotNilOrEmpty, frDateToMiddayDateString)
});

export const formatCustomerBeforeSubmit = evolve({
  birthDate: when(isNotNilOrEmpty, frDateToMiddayDateString),
  socialSecurities: when(isNotNilOrEmpty, map(formatSocialSecurityBeforeSubmit))
});

export const makeFormatGranteeBeforeSubmit = (type) =>
  evolve({
    birthDate: when(isNotNilOrEmpty, frDateToMiddayDateString),
    type: when(isNilOrEmpty, always(type))
  });

const convertTimestampToDate = (timestampStr) => {
  if (!timestampStr || timestampStr === 'null') return null;
  const timestamp = parseInt(timestampStr);
  return !isNaN(timestamp) ? new Date(timestamp) : null;
};

export const formatProjectBeforeSubmit = (values, getMiddleText) => {
  const spouseLabel = getMiddleText('BENEFICIAIRE_TYPE_SPOUSE_LABEL');
  const type = isNotNilOrEmpty(spouseLabel) ? 'SPOUSE' : 'CHILD';
  const formatGranteeBeforeSubmit = makeFormatGranteeBeforeSubmit(type);

  const formatted = compose(
    omit(['offer', 'jobs']),
    evolve({
      customer: when(isNotNilOrEmpty, formatCustomerBeforeSubmit),
      grantees: when(isNotNilOrEmpty, map(formatGranteeBeforeSubmit))
    })
  )(values);

  // Convert timestamps to Date objects
  return evolve({
    customer: when(isNotNilOrEmpty, evolve({
      birthDate: convertTimestampToDate,
      socialSecurities: when(isNotNilOrEmpty, map(evolve({
        dateCSS: convertTimestampToDate
      })))
    })),
    grantees: when(isNotNilOrEmpty, map(evolve({
      birthDate: convertTimestampToDate
    })))
  })(formatted);
};

export const getLabelValueFromTable = (labelKey, valueKey, table) => {
  if (!table) { return; }
  return compose(
    map(applySpec({ label: prop(labelKey), value: prop(valueKey) })),
    filter(propEq('AUTORISE', 'true'))
  )(table);
};

export const formatMultiline = (message) =>
  compose(
    map((line) => (
      <React.Fragment key={line}>
        {line}
        <br />
      </React.Fragment>
    )),
    split('\n'),
    defaultTo('')
  )(message);

export const simpleMarkdownToHtml = (markdown) => {
  if (!markdown) return '';
  return markdown
    .replace(/\*\*\*(.+?)\*\*\*/g, '<b><i>$1</i></b>')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\*(.+?)\*/g, '<i>$1</i>')
    .replace(/\n/g, '<br/>');
};
